import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  calculateBudgetFromImpressions,
  calculateImpressionsFromBudget
} from '../../../../UploadAdCreative/ImpressionsBudgetSection/helpers'
import { calc, formatNumber } from '../../../../../../helpers/numbers'

import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'
import { UPDATE_COMBINED_LINE_ITEM } from '../../../../../../modules/actions/combinedData'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { rightSelector } from '../../../../../../modules/selectors/rights'
import { lifetimeDatePresetCombinedLineItemSelector } from '../../../../../../modules/selectors/combinedData'

import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { BUDGET_LIFETIME } from '../../../../fields'
import { IMPRESSIONS } from '../../../../UploadAdCreative/fields'
import { getImpressionsValidationsSchema } from '../../../../../ReusableFormSections/reusableBudgetSections/remainingImpressionsValidation'
import { OBJECTIVE } from '../../../../../Facebook/LineItemForms/LineItemFacebookCreate/LineItemFacebookCreateForm/fields'
import { objectiveLabelsForOptimizedImpressions } from '../fields'

const LineItemsImpressionsBudgetForm = ({
  adAccountId,
  campaign = {},
  lineItem = {},
  remainingImpressions,
  title,
  editItemData,
  ...formProps
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const right = useSelector(rightSelector)
  const { bid_cap: rightBidCap } = right

  const lifetimeDatePresetLineItem = useSelector(lifetimeDatePresetCombinedLineItemSelector(campaign?.id, lineItem?.id))
  const lifetimeDatePresetLineItemSpend = lifetimeDatePresetLineItem?.spend || 0

  const calculatedImpressions = useMemo(() => {
    // calculated_impressions is calculated by the (spend(with maximum date range filter on)/bid cap on right)*1000
    return calc(lifetimeDatePresetLineItemSpend).div(rightBidCap).mul(1000).toDP(0).toNumber()
  }, [lifetimeDatePresetLineItemSpend, rightBidCap])

  const { [OBJECTIVE]: objective } = lineItem

  const objectiveLabel = useMemo(
    () =>
      objectiveLabelsForOptimizedImpressions[objective] ||
      // handle case when objective label is not in the dictionary
      objective.toLowerCase().replace('_', ' '),
    [objective]
  )

  const handleSubmit = useCallback(
    values => {
      const budget = calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })

      const updateLineItemData = {
        account: adAccountId,
        campaign_objective: campaign.objective,
        [BUDGET_LIFETIME]: budget,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(
        updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM, {
          updateType: UPDATE_COMBINED_LINE_ITEM
        })
      )
    },
    [dispatch, title, campaign.objective, lineItem.id, adAccountId, rightBidCap]
  )

  const initialLineItemImpressions = useMemo(
    () =>
      calculateImpressionsFromBudget({
        budget: lineItem[BUDGET_LIFETIME],
        rightBidCap
      }),
    [lineItem, rightBidCap]
  )

  const initialValues = useMemo(() => {
    return {
      [IMPRESSIONS]: initialLineItemImpressions || ''
    }
  }, [initialLineItemImpressions])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getImpressionsValidationsSchema(
      remainingImpressions,
      initialLineItemImpressions,
      calculatedImpressions
    )
  })

  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <p>{t(`Your remaining total is ${formatNumber(remainingImpressions)} impressions`)}</p>
      {!!calculatedImpressions && (
        <p>
          {t(
            `This ad set has delivered ${formatNumber(values[IMPRESSIONS])} ${objectiveLabel} optimised impressions, which is equivalent to ${formatNumber(calculatedImpressions)} impressions. You cannot set the budget lower than this amount.`
          )}
        </p>
      )}
      <Field
        type="number"
        formik={formik}
        name={IMPRESSIONS}
        placeholder={t('Lifetime Impressions')}
        autoComplete="off"
        inputMode="numeric"
      />
    </EditForm>
  )
}

LineItemsImpressionsBudgetForm.propTypes = {
  adAccountId: PropTypes.string,
  campaign: PropTypes.object,
  lineItem: PropTypes.object,
  remainingImpressions: PropTypes.number,
  title: PropTypes.string,
  editItemData: PropTypes.object,
  updateOptions: PropTypes.object
}

export default LineItemsImpressionsBudgetForm
